import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {NzMessageService} from "ng-zorro-antd/message";
import {PerFormaAPIService} from "src/app/services/pfapi.service";
import {FileDownloadService} from "src/app/services/file-download.service";
import {format, isValid} from "date-fns";
@Component({
    selector: "app-arreport",
    templateUrl: "./arreport.component.html",
    styleUrls: ["./arreport.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ARReportComponent implements OnInit, OnDestroy {
    ReportInfo: any = {
        CustomerNumber: "",
        MinCustomerNumber: "",
        MaxCustomerNumber: "",
        Copies: 1,
        Printer: "",
        StatementEndDate: null
    };
    PageTotal;
    JobLogs: any[] = [];
    Results;
    ARData;
    ModalOptions = {animation: false, backdropClass: "backdrop"};
    JobLogFilter = {Search: ""};
    CurrentJob = null;
    // active report tab: "general" or "jobs"
    activeTab = "general";
    // progress percent for the running-report progress bar
    ProgressPercent = 0;
    // Set vars to necessary default values.
    Loading = true;
    Page = 1;
    Jobs: any[] = [];
    // Set paging options.
    DisplayOptions = [5, 10, 20, 50];
    // Set default page.
    PageSize = 10;
    PreviouslyRunning = false;
    RunningReports = "";
    ReportStatus: any = {};
    Interval;

    constructor(
        private api: PerFormaAPIService,
        private modal: NgbModal,
        private message: NzMessageService,
        private fileDownload: FileDownloadService
    ) {}
    ngOnDestroy(): void {
        if (this.Interval) {
            clearInterval(this.Interval);
        }
    }

    ngOnInit(): void {
        const request = {
            Page: this.Page,
            PageSize: this.PageSize
        };

        // Called when the ng-controller first loads.
        this.api.getApiReportArStatus().subscribe(data => {
            this.ARData = data;
            this.api.postApiReportArJobs(request).subscribe(data2 => {
                this.Jobs = data2.Jobs ?? [];
                this.Page = data2.Page;
                this.Results = data2.TotalItems;
                this.PageTotal = data2.TotalPages;
                this.PageSize = data2.PageSize;
                this.Loading = false;
            });
        });

        if (!this.Interval) {
            this.initRefresh();
        }
    }

    initRefresh = () => {
        this.Interval = setInterval(() => {
            this.api.getApiReportArStatus().subscribe(data => {
                if (data.ReportDone == true) {
                    this.ReportStatus = data;
                    if (this.PreviouslyRunning) {
                        this.message.success("A report job has finished", {nzDuration: 30000});
                        this.modal.dismissAll();
                        this.PreviouslyRunning = false;
                        this.Refresh();
                    }
                } else {
                    if (this.PreviouslyRunning == false) {
                        this.PreviouslyRunning = true;
                        this.Refresh();
                    }
                    this.ReportStatus = data;
                    this.Loading = false;
                    const percent = (Number(data.DocumentsDone) / Number(data.DocumentsTodo)) * 100;
                    this.ProgressPercent = isNaN(percent) ? 0 : Math.round(percent);
                }
                this.GetRunningReports();
            });
        }, 1000);
    };

    GetRunningReports = () => {
        if (this.ReportStatus != null && this.ReportStatus.ReportDone != true) {
            this.RunningReports = "1";
        } else {
            this.RunningReports = "";
        }
    };

    OpenEmailPopup = mdl => {
        this.modal.open(mdl, this.ModalOptions);
    };

    ViewJobLogFile = JobLog => {
        this.fileDownload.viewReportJobLogFile("ar", JobLog.JobUniqueID, JobLog.JobLogID);
    };

    ShowJobLog = (mdl, Job) => {
        this.CurrentJob = Job;
        this.JobLogFilter.Search = "";
        this.Loading = true;
        this.api.getApiReportArJobsJobGuidLogs(Job.JobUniqueID).subscribe(data => {
            // Initialize.
            this.modal.open(mdl, this.ModalOptions);
            this.Loading = false;
            this.JobLogs = data;
        });
    };

    ShowJobInfo = (mdl, Job) => {
        this.CurrentJob = Job;
        this.modal.open(mdl, this.ModalOptions);
    };

    RunEmailReport = prms => {
        if (!this.ValidateReportParameters(prms)) return;
        // format from local date parts (not toISOString) so the picked day isn't shifted by UTC
        const statementDate = format(prms.StatementEndDate, "yyyy-MM-dd");
        this.modal.dismissAll();
        const requestData = {
            CustomerNumber: prms.CustomerNumber,
            MinCustomerNumber: prms.MinCustomerNumber,
            MaxCustomerNumber: prms.MaxCustomerNumber,
            StatementDate: statementDate,
            Copies: prms.Copies,
            EmailSubject: prms.EmailSubject,
            EmailBody: prms.EmailBody,
            EmailFrom: prms.EmailFrom,
            Email: true
        };

        this.Loading = true;
        this.api.postApiReportArRun(requestData).subscribe(data => {
            // Initialize.
            this.ngOnInit();
            this.message.success("Report queued successfully");
        });
    };

    ValidateReportParameters = prms => {
        if (!prms.StatementEndDate || !isValid(prms.StatementEndDate)) {
            this.message.error("Please specify a valid statement date");
            return false;
        }
        const hasValue = value => value !== null && value !== undefined && String(value).trim() !== "";
        if (!hasValue(prms.CustomerNumber) && !(hasValue(prms.MinCustomerNumber) && hasValue(prms.MaxCustomerNumber))) {
            this.message.error("Please specify a customer number or customer number range");
            return false;
        }
        return true;
    };

    GetDateFromString = s => {
        if (s != null) {
            const localDate = new Date(s);
            return localDate.toLocaleDateString() + " " + localDate.toLocaleTimeString();
        } else return "";
    };

    GoBack = () => {
        this.Loading = true;
        this.Page--;
        this.ngOnInit();
    };

    GoNext = () => {
        this.Loading = true;
        this.Page++;
        this.ngOnInit();
    };

    DownloadFile = Job => {
        this.fileDownload.downloadReportJobFile("ar", Job.JobUniqueID);
    };

    DeleteJob = Job => {
        this.Loading = true;
        this.api.deleteApiReportArJobsJobGuid(Job.JobUniqueID).subscribe(() => {
            // Initialize.
            this.ngOnInit();
            this.message.success("Report deleted successfully");
        });
    };

    Refresh = () => {
        this.Loading = true;
        // Initialize.
        this.ngOnInit();
    };

    DisplayChanged = () => {
        this.Loading = true;
        // Reset page to 1.
        this.Page = 1;

        // Initialize.
        this.ngOnInit();
    };

    ManageCustomers = () => {
        window.location.href = "/report/arreport/customers";
    };

    ShowJobProgress = modal => {
        this.modal.open(modal, this.ModalOptions);
    };

    RunPrintReport = prms => {
        if (!this.ValidateReportParameters(prms)) return;
        // format from local date parts (not toISOString) so the picked day isn't shifted by UTC
        const statementDate = format(prms.StatementEndDate, "yyyy-MM-dd");
        const requestData = {
            CustomerNumber: prms.CustomerNumber,
            MinCustomerNumber: prms.MinCustomerNumber,
            MaxCustomerNumber: prms.MaxCustomerNumber,
            StatementDate: statementDate,
            Copies: prms.Copies,
            Printer: prms.Printer
        };
        console.log(requestData);
        this.Loading = true;
        this.api.postApiReportArRun(requestData).subscribe(data => {
            // Initialize.
            this.ngOnInit();

            // Clear fields.
            prms.CustomerNumber = null;
            prms.MinCustomerNumber = null;
            prms.MaxCustomerNumber = null;
            prms.StatementEndDate = null;
            prms.Copies = null;
            prms.Printer = null;

            // Notification.
            this.message.success("Report queued successfully");
        });
    };
}
